import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';

const WA_GREEN = '#25D366';

interface WhatsAppFloatingButtonProps {
  /** Número no formato internacional sem "+" (ex: "5511969241378") */
  phoneNumber: string;
  /** Mensagem pré-preenchida ao abrir o chat */
  initialMessage?: string;
  /** Texto do tooltip exibido no hover */
  tooltipText?: string;
  /** Callback para analytics */
  onClickCallback?: () => void;
  className?: string;
}

const TOOLTIP_VARIANTS = {
  hidden: { x: 10, opacity: 0 },
  visible: {
    x: 0, opacity: 1,
    transition: { type: 'spring' as const, stiffness: 400, damping: 28 },
  },
  exit: {
    x: 8, opacity: 0,
    transition: { duration: 0.1, ease: 'easeIn' as const },
  },
};

const WhatsAppIcon: React.FC = () => (
  <svg width={26} height={26} viewBox="0 0 24 24" fill="white" aria-hidden="true" focusable="false">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const WhatsAppFloatingButton: React.FC<WhatsAppFloatingButtonProps> = ({
  phoneNumber,
  initialMessage = 'Olá! Gostaria de mais informações.',
  tooltipText = 'Falar no WhatsApp',
  onClickCallback,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      setNavOpen((e as CustomEvent<{ open: boolean }>).detail.open);
    };
    window.addEventListener('nav:menu', handler);
    return () => window.removeEventListener('nav:menu', handler);
  }, []);

  useEffect(() => {
    const BASE = 32;
    const MARGIN = 16;
    const ref = containerRef.current;
    const footer = document.querySelector('footer');
    if (!footer || !ref) return;

    // 101 thresholds (0%–100%) so the observer fires for every ~1% of footer visibility,
    // giving smooth repositioning without a scroll listener forcing reflows on the main thread.
    const thresholds = Array.from({ length: 101 }, (_, i) => i / 100);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const overlap = window.innerHeight - entry.boundingClientRect.top;
          ref.style.bottom = `${Math.max(BASE, overlap + MARGIN)}px`;
        } else {
          ref.style.bottom = `${BASE}px`;
        }
      },
      { threshold: thresholds },
    );

    observer.observe(footer);
    ref.style.bottom = `${BASE}px`;
    return () => observer.disconnect();
  }, []);

  // A mensagem leva só o texto do cliente — o rastreamento de origem viaja no
  // evento conversion_click, nunca dentro do que a pessoa envia.
  const clean = phoneNumber.replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${clean}?text=${encodeURIComponent(initialMessage)}`;

  const handleClick = useCallback(() => {
    onClickCallback?.();
  }, [onClickCallback]);

  return (
    <div
      ref={containerRef}
      className="fixed z-50 right-8"
      style={{ bottom: 32 }}
    >
    <AnimatePresence>
    {!navOpen && (
    <motion.div
      key="whatsapp-fab"
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.7 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={cn('flex items-center gap-3', className)}
      role="complementary"
      aria-label="Contato via WhatsApp"
    >
      {/* Tooltip simples — aparece à esquerda no hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            variants={TOOLTIP_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="tooltip"
            id="wa-tooltip"
            className="px-4 py-2 rounded-full bg-white pointer-events-none select-none whitespace-nowrap"
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}
          >
            <span className="text-[13px] font-medium text-gray-700">{tooltipText}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Botão circular */}
      <div className="relative">
        {/* Pulse ring externo */}
        <motion.div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ background: WA_GREEN }}
          animate={{ scale: [1, 1.7, 2.1], opacity: [0.4, 0.1, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut' }}
          aria-hidden="true"
        />
        {/* Pulse ring interno — defasado */}
        <motion.div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ background: WA_GREEN }}
          animate={{ scale: [1, 1.4, 1.7], opacity: [0.3, 0.07, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut', delay: 1.1 }}
          aria-hidden="true"
        />

        <motion.a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tooltipText}
          aria-describedby={isHovered ? 'wa-tooltip' : undefined}
          onClick={handleClick}
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          className="relative w-14 h-14 rounded-full flex items-center justify-center cursor-pointer
            focus-visible:[outline:2px_solid_#25D366] focus-visible:outline-offset-4"
          style={{
            background: WA_GREEN,
            boxShadow: '0 4px 16px rgba(37,211,102,0.45)',
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 420, damping: 18 }}
        >
          <WhatsAppIcon />
        </motion.a>
      </div>
    </motion.div>
    )}
    </AnimatePresence>
    </div>
  );
};

export default WhatsAppFloatingButton;
